import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import api from 'api';
import { generateRandomKey } from 'lib/helpers';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { Text } from '.';

import type { FormApi } from 'informed';

interface Props {
  autoComplete?: string;
  disabled?: boolean;
  field: string;
  formApi?: FormApi<any>;
  id?: string;
  inputContainerRef?: any;
  inputStyle?: React.CSSProperties;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  passwordDisallowEmail?: boolean;
  passwordDisallowUsername?: boolean;
  placeholder?: string;
  required?: boolean;
  showPasswordInitial?: boolean;
  showGenerateOption?: boolean;
  style?: React.CSSProperties;
  userEmailAddress?: string;
  userUsername?: string;
  validateAdditional?: (value: string) => string | undefined;
  validateNewPassword?: boolean;
}

const Password = ({
  autoComplete,
  disabled,
  field,
  formApi,
  id,
  inputContainerRef,
  inputStyle,
  onChange,
  onValueChange,
  passwordDisallowEmail = true,
  passwordDisallowUsername = true,
  placeholder,
  required = true,
  showPasswordInitial = false,
  showGenerateOption,
  style,
  userEmailAddress,
  userUsername,
  validateAdditional,
  validateNewPassword
}: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  const [isHidden, setIsHidden] = useState(!showPasswordInitial);

  const handleGenerate = () => {
    const password = generateRandomKey(serverConfig.passwordMinimumLength);
    formApi.setValue(field, password);
  };

  const toggleVisibility = () => {
    setIsHidden(!isHidden);
  };

  const validateStrength = async (value: string) => {
    const response = await api.post('/auth/local/validate-password-strength', {
      password: value
    });

    const error =
      response.data && response.data.isValid
        ? undefined
        : messages.authPasswordStrengthError;
    formApi.setError(field, error);
  };

  const validate = (value: string) => {
    const {
      passwordDisallowWeakPasswords,
      passwordMaximumLength,
      passwordMinimumLength,
      passwordRequireLettersAndNumeral
    } = serverConfig;

    if (!value || !value.length) {
      return required ? messages.commonFieldRequired : undefined;
    }

    if (value.includes('password')) {
      return getMessageWithPlaceholders({
        message: messages.authPasswordContainsWordError,
        placeholders: { word: 'password' }
      });
    }

    // control relevant validation rules
    if (validateNewPassword) {
      if (passwordMaximumLength && value.length > passwordMaximumLength) {
        return getMessageWithPlaceholders({
          message: messages.authPasswordLengthErrorMax,
          placeholders: { length: passwordMaximumLength }
        });
      }

      if (passwordMinimumLength && value.length < passwordMinimumLength) {
        return (
          `${messages.authPasswordLengthError} ${passwordMinimumLength} ` +
          messages.authPasswordLengthCharacters
        );
      }

      if (
        passwordRequireLettersAndNumeral &&
        !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/.test(value)
      ) {
        return messages.authPasswordLettersAndNumeralError;
      }

      if (passwordDisallowEmail && userEmailAddress?.includes(value)) {
        return messages.authPasswordEmailError;
      }

      if (passwordDisallowUsername && userUsername?.includes(value)) {
        return messages.authPasswordUsernameError;
      }

      if (passwordDisallowWeakPasswords) {
        validateStrength(value);
        const errors = formApi.getState().errors;
        return errors ? errors[field] : undefined;
      }
    }

    if (validateAdditional) return validateAdditional(value);

    return undefined;
  };

  return (
    <div className="password" style={style}>
      <div className="input">
        <Text
          autoComplete={autoComplete}
          disabled={disabled}
          field={field}
          iconStyle={{ right: 36, top: 9 }}
          id={id}
          inputContainerRef={inputContainerRef}
          inputStyle={{
            padding: '7px 40px 7px 10px',
            ...inputStyle
          }}
          onChange={onChange}
          onValueChange={onValueChange}
          placeholder={placeholder}
          type={isHidden ? 'password' : 'text'}
          validate={validate}
          validateOnChange={true}
        />
        <button
          aria-label={isHidden ? messages.commonUnhide : messages.commonHide}
          className="toggle-visibility"
          data-tooltip={isHidden ? messages.commonUnhide : messages.commonHide}
          disabled={disabled}
          onClick={toggleVisibility}
          type="button"
        >
          <Icon icon={isHidden ? 'eye' : 'eye-slash'} />
        </button>
      </div>
      {showGenerateOption ? (
        <Button
          category="light"
          disabled={disabled}
          icon="key"
          aria-label={messages.createAccountRandomPassword}
          data-tooltip={messages.createAccountRandomPassword}
          onClick={handleGenerate}
          style={{ flexShrink: 0, marginTop: 1 }}
        />
      ) : null}

      <style jsx>{`
        .password {
          display: flex;
          gap: 5px;
        }
        .input {
          position: relative;
          width: 100%;
        }
        .toggle-visibility {
          background-color: transparent;
          border: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          font-size: 1.4rem;
          outline: 0;
          position: absolute;
          text-align: center;
          transition: color 0.3s;
          top: 9px;
          right: 4px;
          width: 30px;
        }
        .toggle-visibility:focus,
        .toggle-visibility:hover {
          color: ${theme.textColorLight};
        }
        .toggle-visibility:disabled {
          cursor: default;
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

export default Password;
